import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { MARKET_CATEGORIES, ITEM_CONDITIONS } from "@/lib/market";
import PhotoDrop from "@/components/PhotoDrop";
import { Tag, Loader2, X, Truck, ShieldCheck } from "lucide-react";

export default function MarketCreate() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const editId = sp.get("edit");

  const [form, setForm] = useState({
    title: "",
    category: "Electronics",
    description: "",
    price: "",
    currency: "USD",
    images: [],
    location: "",
    condition: "good",
    shipping_available: false,
    shipping_fee: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!editId) return;
    api.get(`/marketplace/${editId}`).then(r => {
      const i = r.data || {};
      setForm({
        title: i.title || "",
        category: i.category || "Electronics",
        description: i.description || "",
        price: i.price ?? "",
        currency: i.currency || "USD",
        images: i.images || [],
        location: i.location || "",
        condition: i.condition || "good",
        shipping_available: !!i.shipping_available,
        shipping_fee: i.shipping_fee ?? "",
      });
    }).catch(e => setErr(formatErr(e)));
  }, [editId]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const rmImg = (idx) => set("images", form.images.filter((_, k) => k !== idx));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const payload = {
        ...form,
        price: Number(form.price || 0),
        shipping_fee: form.shipping_available ? Number(form.shipping_fee || 0) : 0,
      };
      if (editId) { await api.patch(`/marketplace/${editId}`, payload); nav(`/market/${editId}`); }
      else {
        const { data } = await api.post("/marketplace", payload);
        nav(`/market/${data.id}`);
      }
    } catch (e) { setErr(formatErr(e)); }
    finally { setBusy(false); }
  };

  if (!user) return <div className="max-w-md mx-auto p-10 text-center"><p>Please sign in.</p></div>;

  return (
    <div className="max-w-2xl mx-auto px-6 sm:px-8 py-10" data-testid="market-create-page">
      <p className="overline mb-1">{editId ? "Edit listing" : "New listing"}</p>
      <h1 className="h-display text-4xl">Sell to a real human.</h1>

      <form onSubmit={submit} className="card p-6 mt-7 space-y-5" data-testid="market-form">
        <label className="block">
          <span className="text-xs uppercase tracking-wider text-[#4A4A4A]">Title</span>
          <input className="input mt-1.5" required minLength={3} maxLength={120}
            value={form.title} onChange={(e)=>set("title", e.target.value)}
            placeholder="e.g. 2018 Honda Civic, low miles" data-testid="market-title-input"/>
        </label>

        <div className="grid sm:grid-cols-2 gap-4">
          <label className="block">
            <span className="text-xs uppercase tracking-wider text-[#4A4A4A]">Category</span>
            <select className="input mt-1.5" value={form.category}
              onChange={(e)=>set("category", e.target.value)} data-testid="market-category-select">
              {MARKET_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-wider text-[#4A4A4A]">Condition</span>
            <select className="input mt-1.5" value={form.condition}
              onChange={(e)=>set("condition", e.target.value)} data-testid="market-condition-select">
              {ITEM_CONDITIONS.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
          </label>
        </div>

        <label className="block">
          <span className="text-xs uppercase tracking-wider text-[#4A4A4A]">Description</span>
          <textarea required rows={5} minLength={10} maxLength={4000} className="input mt-1.5"
            value={form.description} onChange={(e)=>set("description", e.target.value)}
            placeholder="Tell the buyer everything they need to know — condition, history, why you're selling, etc."
            data-testid="market-description-input"/>
        </label>

        <div className="grid sm:grid-cols-3 gap-3">
          <label className="block">
            <span className="text-xs uppercase tracking-wider text-[#4A4A4A]">Price</span>
            <input className="input mt-1.5" type="number" min="0" step="1" required
              value={form.price} onChange={(e)=>set("price", e.target.value)} data-testid="market-price-input"/>
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-wider text-[#4A4A4A]">Currency</span>
            <input className="input mt-1.5" maxLength={6} value={form.currency}
              onChange={(e)=>set("currency", e.target.value.toUpperCase())} data-testid="market-currency"/>
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-wider text-[#4A4A4A]">Location</span>
            <input className="input mt-1.5" value={form.location}
              onChange={(e)=>set("location", e.target.value)}
              placeholder="City, Country" data-testid="market-location-input"/>
          </label>
        </div>

        {/* Shipping (always available since all payments are Stripe) */}
        <div className="card p-4">
          <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={form.shipping_available}
              onChange={(e)=>set("shipping_available", e.target.checked)} data-testid="market-shipping-toggle"/>
            <Truck size={14}/> Offer shipping
          </label>
          {form.shipping_available && (
            <label className="block mt-3">
              <span className="text-xs uppercase tracking-wider text-[#4A4A4A]">Shipping fee</span>
              <input type="number" min="0" step="1" className="input mt-1.5"
                value={form.shipping_fee} onChange={(e)=>set("shipping_fee", e.target.value)} data-testid="market-shipping-fee"/>
            </label>
          )}
        </div>

        {/* Photos — Airbnb-style 8-tile grid with auto-add on upload */}
        <div data-testid="market-photos-section">
          <div className="flex items-baseline justify-between">
            <span className="text-xs uppercase tracking-wider text-[#4A4A4A]">Photos · {form.images.length}/8</span>
            <span className="text-[11px] text-[#8A8A8A]">First photo is the cover</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2" data-testid="market-images">
            {form.images.map((src, k) => (
              <div key={k} className={`relative aspect-square rounded-xl overflow-hidden bg-[#F2F2F0] ${k===0 ? "ring-2 ring-[#0A0A0A]" : ""}`}>
                <img src={src} alt="" className="w-full h-full object-cover"/>
                {k === 0 && (
                  <span className="absolute top-1.5 left-1.5 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[#0A0A0A] text-white">Cover</span>
                )}
                <button type="button" onClick={()=>rmImg(k)}
                  className="absolute top-1.5 right-1.5 h-7 w-7 rounded-full bg-black/60 text-white inline-flex items-center justify-center hover:bg-[#B84B4B]"
                  data-testid={`market-image-rm-${k}`}>
                  <X size={12}/>
                </button>
              </div>
            ))}
            {form.images.length < 8 && (
              <div className="aspect-square">
                <PhotoDrop
                  multiple
                  onPick={(url)=>setForm(f => f.images.length >= 8 ? f : ({ ...f, images: [...f.images, url] }))}
                  aspect="square"
                  label={form.images.length === 0 ? "Add photos" : "Add more"}
                  testid="market-photo-drop"
                  maxSize={1400}
                />
              </div>
            )}
          </div>
          <p className="text-[11px] text-[#8A8A8A] mt-2">Tip: select multiple photos at once — they&apos;ll upload and show as tiles instantly.</p>
        </div>

        {err && <p className="text-sm text-[#B84B4B]" data-testid="market-form-error">{err}</p>}
        <p className="text-xs text-[#8A8A8A] inline-flex items-center gap-1">
          <ShieldCheck size={12}/> All sales go through Hoomanz Stripe checkout. 2 % Hoomanz Tip added on top.
        </p>

        <div className="flex gap-2 pt-2">
          <button type="button" onClick={()=>nav("/market")} className="btn-ghost flex-1">Cancel</button>
          <button className="btn-primary flex-1" disabled={busy} data-testid="market-submit">
            {busy ? <><Loader2 size={14} className="animate-spin"/> Publishing…</> : <><Tag size={14}/> {editId ? "Save changes" : "Publish listing"}</>}
          </button>
        </div>
      </form>
    </div>
  );
}
